import { useState, useEffect, useMemo } from "react";
import { Input, Tree } from "antd";
import type { DataNode } from "antd/es/tree";
import type { Key } from "rc-tree/lib/interface";
import { getCreatorsCategories } from "services/SauceService";
import { BaseFilterProps } from "interfaces/filter-component-base";
import {
  transformToTreeData,
  type CategoryItem,
} from "helpers/categories-helper";
import "./CategoriesTreeFilter.scss";

const distributeVertically = <T,>(array: T[], numColumns: number): T[][] => {
  const itemsPerColumn = Math.ceil(array.length / numColumns);

  return Array.from({ length: numColumns }, (_, index) => {
    const start = index * itemsPerColumn;
    const end = start + itemsPerColumn;
    return array.slice(start, end);
  });
};

const distributeWithFoodFirst = (
  nodes: DataNode[],
  numColumns: number
): DataNode[][] => {
  const foodNode = nodes.find((node) => node.title === "Food");
  const otherNodes = nodes.filter((node) => node.title !== "Food");

  if (!foodNode) {
    return distributeVertically(nodes, numColumns);
  }

  const columns: DataNode[][] = [[foodNode]];

  if (numColumns > 1 && otherNodes.length > 0) {
    const remainingColumns = distributeVertically(otherNodes, numColumns - 1);
    columns.push(...remainingColumns);
  } else {
    for (let i = 1; i < numColumns; i++) {
      columns.push([]);
    }
  }

  return columns;
};

const nodeOrChildrenMatch = (node: DataNode, searchTerm: string): boolean => {
  const title = String(node.title || "");
  if (title.toLowerCase().includes(searchTerm.toLowerCase())) {
    return true;
  }
  if (node.children) {
    return node.children.some((child) =>
      nodeOrChildrenMatch(child, searchTerm)
    );
  }
  return false;
};

const markHiddenNodes = (nodes: DataNode[], searchTerm: string): DataNode[] => {
  if (!searchTerm.trim()) {
    return nodes.map((node) => ({
      ...node,
      children: node.children
        ? markHiddenNodes(node.children, searchTerm)
        : undefined,
      className: undefined,
    }));
  }

  return nodes.map((node) => {
    const processedChildren = node.children
      ? markHiddenNodes(node.children, searchTerm)
      : undefined;

    const shouldShow = nodeOrChildrenMatch(node, searchTerm);

    return {
      ...node,
      children: processedChildren,
      className: shouldShow ? undefined : "tree-node-hidden",
    };
  });
};

interface CategoriesTreeFilterProps extends BaseFilterProps {
  numColumns?: number;
}

const CategoriesTreeFilter = (props: CategoriesTreeFilterProps) => {
  const { onChangeValues, values, numColumns = 3 } = props;

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [treeDataColumns, setTreeDataColumns] = useState<DataNode[][]>([]);
  const [checkedKeys, setCheckedKeys] = useState<{ [id: number]: Key[] }>({});
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response: any = await getCreatorsCategories();
        setCategories(response || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      const transformed = transformToTreeData(categories);
      const columns = distributeWithFoodFirst(transformed, numColumns);
      setTreeDataColumns(columns);
    }
  }, [categories, numColumns]);

  useEffect(() => {
    if (!treeDataColumns.length) return;

    const getAllKeysFromColumn = (nodes: DataNode[]): Key[] => {
      const keys: Key[] = [];
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children) {
          keys.push(...getAllKeysFromColumn(node.children));
        }
      });
      return keys;
    };

    const getKeysForColumn = (allValues: Key[], columnIndex: number): Key[] => {
      if (!treeDataColumns[columnIndex]) return [];

      const columnKeys = new Set(
        getAllKeysFromColumn(treeDataColumns[columnIndex])
      );
      return allValues.filter((key) => columnKeys.has(key));
    };

    let allValues: Key[] = [];

    if (values && Array.isArray(values)) {
      allValues = values;
    } else if (values && typeof values === "string") {
      allValues = values.split(";");
    }

    const keys: { [id: number]: Key[] } = {};
    for (let i = 0; i < numColumns; i++) {
      keys[i] = getKeysForColumn(allValues, i);
    }
    setCheckedKeys(keys);
  }, [values, treeDataColumns, numColumns]);

  const filteredTreeDataColumns = useMemo(
    () => treeDataColumns.map((column) => markHiddenNodes(column, searchValue)),
    [treeDataColumns, searchValue]
  );

  const handleCheck = (
    treeIndex: number,
    checked: Key[] | { checked: Key[]; halfChecked: Key[] }
  ) => {
    const checkedKeysArray = Array.isArray(checked) ? checked : checked.checked;
    const uniqueCheckedKeysArray = Array.from(new Set(checkedKeysArray));
    const newCheckedKeys = {
      ...checkedKeys,
      [treeIndex]: uniqueCheckedKeysArray,
    };
    setCheckedKeys(newCheckedKeys);
    if (onChangeValues) {
      const allCheckedKeys = Object.values(newCheckedKeys).flat();
      const uniqueAllCheckedKeys = Array.from(new Set(allCheckedKeys));
      const optimized = uniqueAllCheckedKeys.filter((key) => {
        const keyStr = String(key);
        if (keyStr.includes("|")) {
          const parentKey = keyStr.split("|")[0];
          return !uniqueAllCheckedKeys.includes(parentKey);
        }
        return true;
      });
      onChangeValues(optimized);
    }
  };

  return (
    <div className="categories-tree-filter">
      <div className="categories-tree-filter-search">
        <Input
          placeholder="Search categories..."
          allowClear
          className="categories-search-input"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <div className="categories-tree-filter-content">
        {loading ? (
          <div className="loading-state">Loading categories...</div>
        ) : (
          <div
            className="categories-grid"
            style={{
              gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
            }}
          >
            {filteredTreeDataColumns.map((column, index) => (
              <Tree
                key={index}
                checkable
                selectable={false}
                treeData={column}
                showIcon
                className="categories-tree"
                expandedKeys={column?.map((node) => node.key)}
                checkedKeys={checkedKeys[index]}
                onCheck={(checked) => handleCheck(index, checked)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesTreeFilter;
